// services/isvpayWebSocketService.js
import localRequest from './LocalServer/localRequest';
import logger from './LocalServer/logger';

class ISVPayWebSocketService {
  constructor() {
    this.ws = null;
    this.config = null;
    this.messageHandlers = new Map();
    this.isConnected = false;
    this.isInitialized = false;
    this.isDeviceReady = false;
    this.isDeviceConnecting = false; // 防止重复设备连接
    this.connectionPromise = null; // 防止重复连接
  }

  // 获取ISVPay配置
  async getConfig() {
    try {
      const response = await localRequest({
        method: 'get',
        url: '/terminal/config',
      });
      this.config = response.data;
      logger(2, `ISVPay config loaded: ${JSON.stringify(this.config)}`);
      return this.config;
    } catch (error) {
      logger(0, `Failed to get ISVPay config: ${error.message}`);
      throw error;
    }
  }

  // 确保连接就绪（主要入口点）
  async ensureConnection() {
    // 如果已经完全就绪，直接返回
    if (this.isConnected && this.isDeviceReady) {
      logger(2, 'ISVPay already connected and device ready');
      return;
    }

    // 如果正在连接中，等待现有连接完成
    if (this.connectionPromise) {
      logger(2, 'ISVPay connection in progress, waiting...');
      return this.connectionPromise;
    }

    // 开始新的连接流程，带重试机制
    this.connectionPromise = this._performConnectionWithRetry();

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  // 固定间隔重试
  async _performConnectionWithRetry() {
    const maxRetries = 6;
    const retryDelay = 8; // 10秒

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger(2, `ISVPay connection attempt ${attempt}/${maxRetries}`);
        await this._performConnection();
        logger(2, 'ISVPay connection established successfully');
        return;
      } catch (error) {
        logger(0, `ISVPay connection attempt ${attempt} failed: ${error.message}`);

        if (attempt === maxRetries) {
          throw new Error(
            `Failed to establish ISVPay connection after ${maxRetries} attempts: ${error.message}`
          );
        }

        logger(2, `ISVPay waiting ${retryDelay}ms before retry attempt ${attempt + 1}`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }
  }

  // 内部连接实现
  async _performConnection() {
    try {
      if (!this.isConnected) {
        await this.connect();
      }

      if (!this.isDeviceReady) {
        await this.waitForDeviceReady();
      }

      logger(2, 'ISVPay connection fully established');
    } catch (error) {
      logger(0, `ISVPay connection failed: ${error.message}`);
      throw error;
    }
  }

  // 连接WebSocket
  async connect() {
    if (!this.config) {
      await this.getConfig();
    }

    // 清理之前的连接
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      if (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
      this.ws = null;
    }

    return new Promise((resolve, reject) => {
      // 设置连接超时
      const connectTimeout = setTimeout(() => {
        const errorMsg = 'WebSocket connection timeout - ISVPay middleware may not be responding';
        logger(0, `ISVPay WebSocket error: ${errorMsg}`);
        this.isConnected = false;
        if (this.ws) {
          this.ws.close();
        }
        reject(new Error(errorMsg));
      }, 5000); // 5秒超时

      try {
        logger(2, `ISVPay attempting WebSocket connection to: ${this.config.isvpay.websocketUrl}`);
        this.ws = new WebSocket(this.config.isvpay.websocketUrl);

        this.ws.onopen = () => {
          clearTimeout(connectTimeout);
          logger(2, 'ISVPay WebSocket connected');
          this.isConnected = true;
          this.initializeWebSocket();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            logger(2, `ISVPay received: ${JSON.stringify(message)}`);
            this.handleMessage(message);
          } catch (error) {
            logger(0, `Failed to parse ISVPay message: ${error.message}`);
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectTimeout);
          const errorMsg =
            error?.message || 'WebSocket connection failed - ISVPay middleware may not be running';
          logger(0, `ISVPay WebSocket error: ${errorMsg}`);
          this.isConnected = false;
          reject(new Error(errorMsg));
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectTimeout);
          logger(1, `ISVPay WebSocket disconnected - Code: ${event.code}, Reason: ${event.reason}`);
          this.isConnected = false;
          // 如果是非正常关闭，且不是在连接过程中，记录更详细的信息
          if (event.code !== 1000 && event.code !== 1001) {
            logger(1, `ISVPay WebSocket closed unexpectedly - Code: ${event.code}`);
          }
        };
      } catch (error) {
        clearTimeout(connectTimeout);
        logger(0, `ISVPay WebSocket creation failed: ${error.message}`);
        this.isConnected = false;
        reject(error);
      }
    });
  }

  // 初始化WebSocket
  initializeWebSocket() {
    // 步骤1: 初始化WebSocket中间件
    this.sendMessage({
      type: 'REQ_WS_INIT',
      data: {
        mode: this.config.isvpay.sdkMode,
        logLevel: 'LEVEL_FULL',
      },
    });

    // 注意：API Key和Integration ID在服务端配置，前端不需要发送
    // 根据.js demo，这些调用被注释掉了，说明服务端已经配置了这些密钥

    // 步骤2: 等待WebSocket初始化完成后发送终端配置
    // 步骤3: 然后连接设备 - 这些步骤现在由消息响应驱动
  }

  // 连接设备
  connectDevice() {
    // 防止重复连接设备
    if (this.isDeviceConnecting || this.isDeviceReady) {
      logger(2, `ISVPay device already connecting or connected, skipping`);
      return;
    }

    this.isDeviceConnecting = true;

    const deviceConfig = this.config.isvpay.device || {
      device: 'IDTECH',
      connectionType: 'USB',
      inputMethod: 'SWIPE_OR_INSERT_OR_TAP',
      portName: 'COM3',
    };

    logger(2, `ISVPay attempting to connect device with config: ${JSON.stringify(deviceConfig)}`);

    const success = this.sendMessage({
      type: 'REQ_INIT_DEVICE',
      data: deviceConfig,
    });

    if (success) {
      logger(2, `ISVPay device connection message sent successfully`);
    } else {
      logger(0, `ISVPay failed to send device connection message`);
      this.isDeviceConnecting = false; // 重置状态
    }
  }

  // 等待设备就绪
  async waitForDeviceReady() {
    return new Promise((resolve, reject) => {
      if (this.isDeviceReady) {
        resolve();
        return;
      }

      // 设置设备连接监听器
      this.messageHandlers.set('deviceReady', () => {
        this.isDeviceReady = true;
        this.messageHandlers.delete('deviceReady');
        resolve();
      });

      // 设置超时
      setTimeout(() => {
        if (this.messageHandlers.has('deviceReady')) {
          this.messageHandlers.delete('deviceReady');
          reject(new Error('Device connection timeout'));
        }
      }, 20000); // 20秒超时 - 重启后设备需要更长时间初始化
    });
  }

  // 发送消息
  sendMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const messageStr = JSON.stringify(message);
      logger(2, `ISVPay sending: ${messageStr}`);
      this.ws.send(messageStr);
      return true;
    } else {
      logger(0, 'ISVPay WebSocket not ready for sending messages');
      return false;
    }
  }

  // 处理接收到的消息
  handleMessage(message) {
    const { type, data } = message;

    switch (type) {
      case 'RES_ON_SALE_RESPONSE':
        this.handleSaleResponse(data);
        break;
      case 'RES_ON_MESSAGE':
        this.handleStatusMessage(data);
        break;
      case 'RES_ON_ERROR':
        this.handleError(data);
        break;
      case 'RES_ON_WS_INIT':
        logger(2, 'ISVPay WebSocket initialized successfully');
        this.isInitialized = true;
        // 步骤2: WebSocket初始化完成后，发送终端配置
        this.sendMessage({
          type: 'REQ_INIT_WITH_CONFIGURATION',
          data: {
            terminalId: this.config.isvpay.terminalId,
            processOfflineWhenCommsDown: false,
          },
        });
        break;
      case 'RES_ON_INIT_WITH_CONFIGURATION':
        logger(2, 'ISVPay terminal configuration completed');
        // 步骤3: 终端配置完成后，连接设备
        setTimeout(() => {
          this.connectDevice();
        }, 500); // 稍微等待一下
        break;
      case 'RES_ON_SETTINGS_RETRIEVED':
        logger(2, 'ISVPay settings retrieved, proceeding to connect device');
        // 收到设置信息后，也触发设备连接
        setTimeout(() => {
          this.connectDevice();
        }, 500);
        break;
      case 'RES_ON_DEVICE_CONNECTED':
        logger(2, `ISVPay device connected: ${JSON.stringify(data)}`);
        this.isDeviceReady = true;
        this.isDeviceConnecting = false; // 连接成功，重置状态
        // 触发设备就绪回调
        const deviceReadyHandler = this.messageHandlers.get('deviceReady');
        if (deviceReadyHandler) {
          deviceReadyHandler();
        }
        break;
      case 'RES_ON_DEVICE_DISCONNECTED':
        logger(2, `ISVPay device disconnected: ${JSON.stringify(data)}`);
        this.isDeviceReady = false;
        this.isDeviceConnecting = false; // 重置连接状态
        break;
      case 'RES_ON_DEVICE_CONNECTION_ERROR':
        logger(0, `ISVPay device connection error: ${JSON.stringify(data)}`);
        this.isDeviceReady = false;
        this.isDeviceConnecting = false; // 连接失败，重置状态
        break;
      case 'RES_ON_DEVICE_ERROR':
        logger(1, `ISVPay device error: ${JSON.stringify(data)}`);

        // 处理交易正在进行中的错误
        if (data?.deviceError === 'TRANSACTION_IN_PROGRESS') {
          logger(1, 'ISVPay transaction in progress - will cancel and retry with new transaction');
          const errorHandler = this.messageHandlers.get('error');
          if (errorHandler) {
            errorHandler({
              errorMessage: 'Previous transaction in progress, cancelling...',
              deviceError: 'TRANSACTION_IN_PROGRESS',
            });
          }
        } else {
          // 其他设备错误的处理
          const errorHandler = this.messageHandlers.get('error');
          if (errorHandler) {
            errorHandler(data);
          }
        }

        // 如果是设备初始化错误，不重置连接状态，让它继续等待
        if (data?.deviceError !== 'DEVICE_INITIALIZING') {
          this.isDeviceConnecting = false;
        }
        break;
      case 'RES_ON_WS_INIT_REQUIRED':
        logger(2, `ISVPay WebSocket initialization required: ${JSON.stringify(data)}`);
        // 这个消息只是通知我们需要初始化，不需要特殊处理
        break;
      case 'RES_ON_CANCEL_TRANSACTION_ATTEMPT':
        logger(2, `ISVPay cancel transaction response: ${JSON.stringify(data)}`);
        // 取消交易的响应，记录即可
        break;
      default:
        logger(2, `ISVPay unhandled message type: ${type}, data: ${JSON.stringify(data)}`);
    }
  }

  // 处理销售响应
  handleSaleResponse(data) {
    logger(2, `ISVPay sale response: ${JSON.stringify(data)}`);
    const handler = this.messageHandlers.get('sale');
    if (handler) {
      handler(data);
      this.messageHandlers.delete('sale');
    }
  }

  // 处理状态消息
  handleStatusMessage(data) {
    logger(2, `ISVPay status message: ${JSON.stringify(data)}`);
    const handler = this.messageHandlers.get('status');
    if (handler) {
      handler(data);
    }
  }

  // 处理错误
  handleError(data) {
    logger(0, `ISVPay error: ${JSON.stringify(data)}`);
    const handler = this.messageHandlers.get('error');
    if (handler) {
      handler(data);
    }
  }

  // 开始销售
  async startSale(amount, statusCallback) {
    // 确保设备完全就绪
    if (!this.isConnected || !this.isDeviceReady) {
      throw new Error('Device not ready for sale. Please ensure connection first.');
    }

    return new Promise((resolve, reject) => {
      const saleData = {
        amount: amount.toString(),
        transactionInputMethod: 'SWIPE_OR_INSERT_OR_TAP',
        signatureCollection: 'AUTOMATIC',
      };

      // 设置响应处理器
      this.messageHandlers.set('sale', (data) => {
        if (data && (data.status === 'APPROVED' || data.approved)) {
          resolve({
            success: true,
            message: 'Payment successful',
            transactionData: data,
            paymentId: data.uniqueRef || data.transactionId,
            authCode: data.authCode,
            cardType: data.cardType,
          });
        } else {
          resolve({
            success: false,
            message: data?.errorMessage || 'Payment failed',
            transactionData: data,
          });
        }
      });

      // 设置状态处理器
      if (statusCallback) {
        this.messageHandlers.set('status', statusCallback);
      }

      // 设置错误处理器
      this.messageHandlers.set('error', (data) => {
        reject(new Error(data.errorMessage || 'Transaction error'));
      });

      // 发送销售请求
      if (
        !this.sendMessage({
          type: 'REQ_PROCESS_SALE',
          data: saleData,
        })
      ) {
        reject(new Error('Failed to send sale request'));
        return;
      }

      // // 设置超时
      // setTimeout(() => {
      //   if (this.messageHandlers.has('sale')) {
      //     this.messageHandlers.delete('sale');
      //     this.messageHandlers.delete('error');
      //     this.messageHandlers.delete('status');
      //     reject(new Error('Transaction timeout'));
      //   }
      // }, 60000); // 60秒超时
    });
  }

  // 取消交易
  async cancelTransaction() {
    return new Promise((resolve) => {
      this.sendMessage({
        type: 'REQ_CANCEL_TRANSACTION',
      });

      // ISVPay 取消通常是立即的
      setTimeout(() => {
        resolve({
          success: true,
          message: 'Transaction cancelled',
        });
      }, 1000);
    });
  }

  // 断开连接
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
    this.messageHandlers.clear();
  }

  // 检查连接状态
  isConnectedToWebSocket() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  // 检查是否完全就绪（WebSocket + 设备）
  isReady() {
    return this.isConnected && this.isDeviceReady;
  }

  // 获取连接状态信息
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isInitialized: this.isInitialized,
      isDeviceReady: this.isDeviceReady,
      isReady: this.isReady(),
    };
  }
}

export default new ISVPayWebSocketService();
